import * as React from "react";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/molecules/Dialog";
import {
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
} from "@/components/molecules/Popover";
import QuestionIcon from "@/icons/__generated__/Question/Question16";
import { useMediaQuery } from "@/lib/hooks/use-media-query";
import { cn } from "@/lib/utils";
import type { PriceLineItemTooltip } from "./PriceLineItem.types";
import { PriceLineItemTooltipCard } from "./PriceLineItemTooltipCard";

type PriceLineItemLabelProps = {
  colorClass: string;
  label: React.ReactNode;
  testId?: string;
  tooltip?: PriceLineItemTooltip;
  typefaceClass: string;
};

/**
 * Label track content with optional label-level tooltip trigger.
 */
const PriceLineItemLabel = ({
  colorClass,
  label,
  testId,
  tooltip,
  typefaceClass,
}: PriceLineItemLabelProps) => {
  const [tooltipOpen, setTooltipOpen] = React.useState(false);
  const isMobile = useMediaQuery("(max-width: 576px)", {
    isActive: tooltip?.presentation === "modal",
  });

  if (!tooltip) {
    return (
      <div
        className={cn(typefaceClass, colorClass)}
        data-testid={testId ? `${testId}-name` : undefined}
      >
        {label}
      </div>
    );
  }

  const closeTooltip = () => setTooltipOpen(false);
  const resolvedContent =
    !tooltip.renderContent && !tooltip.onClick
      ? typeof tooltip.content === "function"
        ? tooltip.content(closeTooltip)
        : tooltip.content
      : undefined;

  const triggerContent = (
    <span
      className={cn(
        // Block-level flex, not inline-flex: an inline box gets baseline-aligned
        // in the button's line box and lands ~1.5px below the value and the dot.
        // min-h preserves typeface line-height so the left-offset dot stays centered.
        "tw-underline sm:tw-no-underline tw-flex tw-items-start tw-gap-4 tw-min-h-[1lh]",
        typefaceClass,
        colorClass,
      )}
    >
      <span>{label}</span>
      <QuestionIcon
        // Centered on the first line rather than on the whole label, so a
        // wrapped label doesn't leave the icon floating between its lines
        className="tw-hidden sm:tw-block tw-shrink-0 tw-mt-[calc((1lh-16px)/2)]"
        name="question-mark"
      />
    </span>
  );

  if (tooltip.onClick) {
    return (
      <div data-testid={testId ? `${testId}-name` : undefined}>
        <button
          type="button"
          className="tw-text-left tw-align-top"
          onClick={tooltip.onClick}
        >
          {triggerContent}
        </button>
      </div>
    );
  }

  const trigger = (
    <button
      type="button"
      // The trigger is an inline-block in the label cell's line box; align-top
      // keeps it off the baseline so it starts at the cell's top edge
      className="tw-text-left tw-align-top"
    >
      {triggerContent}
    </button>
  );

  const popover = (
    <Popover
      open={tooltipOpen}
      onOpenChange={setTooltipOpen}
      isResponsive={true}
    >
      <PopoverTrigger asChild={true}>{trigger}</PopoverTrigger>
      <PopoverContent
        align="start"
        side="top"
        className="tw-min-h-[193px] sm:tw-min-h-0"
      >
        <PriceLineItemTooltipCard
          title={tooltip.title}
          content={
            tooltip.renderContent
              ? tooltip.renderContent(setTooltipOpen)
              : resolvedContent
          }
        />
        <PopoverArrow className="tw-fill-base-white" />
      </PopoverContent>
    </Popover>
  );

  if (tooltip.presentation !== "modal" || isMobile) {
    return (
      <div data-testid={testId ? `${testId}-name` : undefined}>{popover}</div>
    );
  }

  return (
    <div data-testid={testId ? `${testId}-name` : undefined}>
      <Dialog open={tooltipOpen} onOpenChange={setTooltipOpen}>
        <DialogTrigger asChild={true}>{trigger}</DialogTrigger>
        <DialogContent size="md">
          <DialogHeader showCloseButton={true}>
            <DialogTitle
              className={cn("tw-m-0", { "tw-sr-only": !tooltip.title })}
            >
              {tooltip.title || "Details"}
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="tw-pb-32">
            {tooltip.renderContent
              ? tooltip.renderContent(setTooltipOpen)
              : resolvedContent}
          </DialogBody>
        </DialogContent>
      </Dialog>
    </div>
  );
};

PriceLineItemLabel.displayName = "PriceLineItemLabel";

export { PriceLineItemLabel, type PriceLineItemLabelProps };
